import { Injectable } from '@angular/core';
import { share } from 'rxjs/operators';
import { SpaContext } from '../../../Classes/spacontext';
import { VisibaApiHttp } from '../../../Classes/visibaHttp.class';
import { AnswerFormQuestionUpdateResult } from './Models/Basket/AnswerFormQuestionUpdateResult';
import { BasketContents } from './Models/Basket/BasketContents';
import { BasketFileReferenceInput } from './Models/Basket/BasketFileReferenceInput';
import { BasketFormAnswer } from './Models/Basket/BasketFormAnswer';
import { CommitResult } from './Models/Basket/CommitResult';
import { CommitSuccessResult } from './Models/Basket/CommitSuccessResult';
import { ExternalDataResult } from './Models/Basket/ExternalDataResult';
import { FileMapResult } from './Models/Basket/FileMapResult';
import { FormQuestionAnswer } from './Models/Basket/FormQuestionAnswer';
import { MessagingConversationInput } from './Models/Basket/MessagingConversationInput';
import { ExternalDataService } from './Units/external-data.service';

@Injectable()
export class BasketService {
	readonly baseUri = '/api/Client/v2/Baskets';

	constructor(
		private http: VisibaApiHttp,
		private spaContext: SpaContext,
		private externalDataService: ExternalDataService,
	) {}

	public getActive() {
		return this.http.getTyped<number>(`${this.baseUri}/Active/${this.spaContext.unitConfig.ReceptionId}`);
	}

	public get(baskedId: number) {
		return this.http.getTyped<BasketContents>(`${this.baseUri}/${baskedId}`);
	}

	public delete(baskedId: number) {
		return this.http.deleteTyped<BasketContents>(`${this.baseUri}/${baskedId}`);
	}

	/**
	 * External data
	 */
	public getExternalData(basketId: number, basketProductId: number) {
		return this.http.getTyped<ExternalDataResult>(`${this.baseUri}/${basketId}/Item/${basketProductId}/ExternalData`);
	}

	public postExternalData(basketId: number, basketProductId: number, externalDataReferences: Guid[]) {
		return this.http.postTyped<void>(`${this.baseUri}/${basketId}/Item/${basketProductId}/ExternalData`, externalDataReferences);
	}

	/**
	 * Items
	 */
	public addProductMessagingConversation(baskedId: number, conversation: MessagingConversationInput) {
		conversation.ExternalDataReferences = this.externalDataService.getExternalDataReferences().map((reference) => reference.guid);
		const observeable = this.http
			.postTyped<BasketContents>(`${this.baseUri}/${baskedId}/MessagingConversation`, conversation)
			.pipe(share());

		observeable.subscribe(() => this.externalDataService.clearExternalDataReferences());

		return observeable;
	}

	public removeItem(baskedId: number, basketItemId: number) {
		return this.http.deleteTyped<void>(`${this.baseUri}/${baskedId}/Items/${basketItemId}`);
	}

	/**
	 * Files
	 */
	public addFiles(baskedId: number, basketItemId: number, files: BasketFileReferenceInput[]) {
		return this.http.postTyped<FileMapResult[]>(`${this.baseUri}/${baskedId}/Item/${basketItemId}/Files`, files);
	}

	public deleteFiles(baskedId: number, basketItemId: number, fileIds: Guid[]) {
		let params = '';
		fileIds.forEach((id) => (params += `fid/${id}/`));

		return this.http.deleteTyped<{ [key: string]: boolean }>(`${this.baseUri}/${baskedId}/Item/${basketItemId}/Files/${params}`);
	}

	/**
	 * Forms
	 */
	public setFormAnswers(baskedId: number, formId: number, basketProductId: number, answers: BasketFormAnswer[]) {
		return this.http.postTyped<AnswerFormQuestionUpdateResult[]>(
			`${this.baseUri}/${baskedId}/FormAnswers/${basketProductId}/${formId}`,
			answers,
		);
	}

	public getFormAnswers(baskedId: number, formId: number, basketProductId: number) {
		return this.http.getTyped<FormQuestionAnswer[]>(`${this.baseUri}/${baskedId}/FormAnswers/${basketProductId}/${formId}`);
	}

	public getFormAnswersByQuestions(baskedId: number, formId: number, basketProductId: number, questionIds: number[]) {
		return this.http.postTyped<FormQuestionAnswer[]>(
			`${this.baseUri}/${baskedId}/GetFormAnswers/${basketProductId}/${formId}`,
			questionIds,
		);
	}

	/**
	 * Commiting
	 */
	public commit(baskedId: number) {
		return this.http.putTyped<CommitResult>(`${this.baseUri}/${baskedId}/Commit`, null);
	}

	public createCommit(baskedId: number) {
		return this.http.putTyped<number>(`${this.baseUri}/${baskedId}/CreateCommit`, null);
	}

	public getCommitStatus(commitJobId: number) {
		return this.http.getTyped<CommitSuccessResult>(`${this.baseUri}/Commit/${commitJobId}`, null);
	}
}
